import Link from "next/link";
import { useRouter } from "next/router";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange?: (page: number) => void;
  generateUrl?: (page: number) => string;
}

export const Pagination = ({ 
  currentPage, 
  totalPages, 
  onPageChange,
  generateUrl 
}: PaginationProps) => {
  const router = useRouter();

  if (totalPages <= 1) return null;

  const maxPagesToShow = totalPages;
  const halfRange = Math.floor(maxPagesToShow / 2);

  let startPage = Math.max(1, currentPage - halfRange);
  let endPage = Math.min(totalPages, currentPage + halfRange);

  if (currentPage - halfRange < 1) {
    endPage = Math.min(totalPages, endPage + (halfRange - currentPage + 1));
  }
  if (currentPage + halfRange > totalPages) {
    startPage = Math.max(1, startPage - (currentPage + halfRange - totalPages));
  }

  const pages = Array.from(
    { length: endPage - startPage + 1 },
    (_, i) => startPage + i
  );

  const getPageUrl = (page: number): string => {
    if (generateUrl) {
      return generateUrl(page);
    }
    
    const query = { ...router.query };
    if (page === 1) {
      delete query.page;
    } else {
      query.page = page.toString();
    }
    
    return {
      pathname: router.pathname,
      query,
    } as any;
  };

  const handlePageClick = (page: number) => {
    if (onPageChange) {
      onPageChange(page);
    }
  };

  const renderPageLink = (page: number, label: string | number, isActive: boolean = false) => {
    const isDisabled = page < 1 || page > totalPages;
    
    if (isDisabled) {
      return (
        <span
          key={`page-${label}`}
          className="px-3 py-2 mx-1 bg-gray-200 text-gray-400 rounded cursor-not-allowed"
        >
          {label}
        </span>
      );
    }

    return (
        <Link
            key={`page-${label}`}
            href={getPageUrl(page)}
            onClick={(e) => {
                if (onPageChange) {
                    e.preventDefault();
                    handlePageClick(page);
                }
            }}
            className={`px-3 py-2 mx-1 rounded transition-colors ${
                isActive
                    ? "bg-primary text-white font-semibold"
                    : "bg-white text-primary border border-primary hover:bg-primary hover:text-white"
            }`}
            aria-label={`Go to page ${page}`}
            aria-current={isActive ? "page" : undefined}
            rel={page === currentPage - 1 ? "prev" : page === currentPage + 1 ? "next" : undefined}
        >
            {label}
        </Link>
    );
  };

  return (
    <nav className="flex items-center justify-center flex-wrap gap-2" aria-label="Pagination">
      {/* Previous Button */}
      {renderPageLink(currentPage - 1, "← Previous")}

      {/* First Page */}
      {startPage > 1 && (
        <>
          {renderPageLink(1, 1)}
          {startPage > 2 && (
            <span className="px-2 py-2 mx-1 text-gray-500">...</span>
          )}
        </>
      )}

      {/* Page Numbers */}
      {pages.map((page) => renderPageLink(page, page, page === currentPage))}

      {/* Last Page */}
      {endPage < totalPages && (
        <>
          {endPage < totalPages - 1 && (
            <span className="px-2 py-2 mx-1 text-gray-500">...</span>
          )}
          {renderPageLink(totalPages, totalPages)}
        </>
      )}

      {/* Next Button */}
      {renderPageLink(currentPage + 1, "Next →")}
    </nav>
  );
};